import React, { useState } from 'react';
import { Modal, Paper, Typography, Button, Box, Chip, IconButton, CircularProgress } from '@mui/material';
import { Close, Cancel, Visibility, CheckCircle, DirectionsCar } from '@mui/icons-material';

interface TaskDetailsModalProps {
  open: boolean;
  onClose: () => void;
  task: {
    uuid: string;
    task_status: {
      status: string;
    };
    task_type?: {
      type: string;
      class: string;
    };
    trailer?: {
      trailer_number: string;
      carrier: {
        name: string;
      };
    };
    from_object?: {
      __typename: string;
      uuid: string;
      name?: string;
    };
    to_object?: {
      __typename: string;
      uuid: string;
      name?: string;
    };
    created_at?: string;
    updated_at?: string;
  } | null;
  onCancelTask?: (taskId: string) => Promise<void>;
  onCompleteTask?: (taskId: string) => Promise<void>;
  onCompleteMovementTask?: (taskId: string) => Promise<void>;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  open,
  onClose,
  task,
  onCancelTask,
  onCompleteTask,
  onCompleteMovementTask
}) => {
  const [cancelling, setCancelling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completingMovement, setCompletingMovement] = useState(false);

  if (!task) return null;

  const handleCancelTask = async () => {
    if (!onCancelTask) return;
    
    setCancelling(true);
    try {
      await onCancelTask(task.uuid);
      onClose();
    } catch (error) {
      console.error('Failed to cancel task:', error);
    } finally {
      setCancelling(false);
    }
  };

  const handleCompleteTask = async () => {
    if (!onCompleteTask) return;
    
    setCompleting(true);
    try {
      await onCompleteTask(task.uuid);
      onClose();
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setCompleting(false);
    }
  };

  const handleCompleteMovementTask = async () => {
    if (!onCompleteMovementTask) return;
    
    setCompletingMovement(true);
    try {
      await onCompleteMovementTask(task.uuid);
      onClose();
    } catch (error) {
      console.error('Failed to complete movement task:', error);
    } finally {
      setCompletingMovement(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'assigned':
        return 'primary';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatObjectName = (obj: any) => {
    if (!obj) return 'Unknown';
    
    const typeName = obj.__typename?.replace('Object', '') || 'Unknown';
    const name = obj.name || obj.uuid?.slice(-8) || 'Unknown';
    
    return `${typeName}: ${name}`;
  };

  const canCancelTask = task.task_status.status !== 'COMPLETED' && task.task_status.status !== 'CANCELLED';
  const canCompleteTask = task.task_status.status !== 'COMPLETED' && task.task_status.status !== 'CANCELLED';
  // Detect movement tasks by task type
  const isMovementTask = task.task_type?.type === 'MOVE_TRAILER';

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="task-details-modal"
      aria-describedby="task-details-description"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          maxHeight: '80vh',
          overflow: 'auto',
          p: 3,
          outline: 'none'
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Task Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Task Information */}
        <Box mb={3}>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Task ID
            </Typography>
            <Typography variant="body1" fontFamily="monospace">
              {task.uuid}
            </Typography>
          </Box>

          {task.task_type && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Task Type
              </Typography>
              <Typography variant="body1">
                {task.task_type.type}
              </Typography>
            </Box>
          )}

          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Chip 
              label={task.task_status.status} 
              color={getStatusColor(task.task_status.status)}
              size="small"
            />
          </Box>

          {task.from_object && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                From
              </Typography>
              <Typography variant="body1">
                {formatObjectName(task.from_object)}
              </Typography>
            </Box>
          )}

          {task.to_object && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                To
              </Typography>
              <Typography variant="body1">
                {formatObjectName(task.to_object)}
              </Typography>
            </Box>
          )}

          {task.trailer && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Trailer
              </Typography>
              <Typography variant="body1">
                {task.trailer.trailer_number}
                {task.trailer.carrier?.name && (
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                    ({task.trailer.carrier.name})
                  </Typography>
                )}
              </Typography>
            </Box>
          )}

          {task.created_at && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created
              </Typography>
              <Typography variant="body1">
                {new Date(task.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}

          {task.updated_at && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(task.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => window.open(`/tasks/${task.uuid}`, '_blank')}
          >
            View Full Details
          </Button>
          
         
          
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TaskDetailsModal; 